import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Link,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../context/AuthContext";
import { useModal } from '../context/ModalContext';

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: 0,
  boxShadow: 24,
  px: 4,
  pb: 4,
  pt: 4,
};

const LoginModal = ({ open, onClose }) => {
  const { login } = useAuth();
  const { loginOpen, openLogin, closeLogin, openSignup } = useModal();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleClose = () => {
    // Reset all input fields and messages
    setEmail("");
    setEmailError("");
    setPassword("");
    setShowPassword(false);
    setError("");
    setSuccess("");
    onClose(); // call parent onClose to actually close the modal
  };


  // Email validation function
  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleLogin = async () => {
    setError("");
    setSuccess("");

    // Validate email before sending request
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (res.ok) {
        login(data.user);
        setSuccess("Login successful!");
        onClose();
      } else {
        setError(data.message || "Invalid credentials");
      }
    } catch (err) {
      setError("Error connecting to server.");
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError(""); // reset error while typing
    setError(""); // clear any previous login errors
  };

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError("Please enter a valid email address");
    }
  };

  const isFormValid =
    email.trim() !== "" &&
    password.trim() !== "" &&
    !emailError &&
    validateEmail(email);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
      }}
      sx={{
    outline: "none", // removes blue focus border around modal
  }}
    >
      <Box
  sx={{
    ...modalStyle,
    outline: "none",
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: "none" },
  }}
  tabIndex={-1} // ensure Box can be focused
>

        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            padding: "4px",
            cursor: "pointer",
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: "12px",
            left: 0,
            width: "100%",
            textAlign: "center",
            fontFamily: "Poppins, sans-serif",
            fontSize: "32px",
            fontWeight: 400,
            color: "#142434",
          }}
        >
          Login
        </Typography>

        {/* Divider */}
        <Divider
          sx={{
            position: "absolute",
            top: "70px",
            left: 0,
            width: "100%",
            borderColor: "#489596",
          }}
        />

        {/* Input fields */}
        <Box display="flex" flexDirection="column" gap={2} mt={7}>
          {/* Email */}
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.5, fontFamily: "PT Sans, sans-serif" }}
            >
              Email
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter your email"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur} // validate on blur
              inputProps={{ style: { fontFamily: "PT Sans", fontSize: 14, color: "#7F8183" } }}
            />
          </Box>

          {/* Password */}
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.5, fontFamily: "PT Sans, sans-serif" }}
            >
              Password
            </Typography>
            <TextField
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              inputProps={{ style: { fontFamily: "PT Sans", fontSize: 14, color: "#7F8183" } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Fixed space for messages */}
          <Box sx={{ minHeight: "24px" }}>
            {(emailError || error || success) && (
              <Typography
                variant="body2"
                sx={{
                  color: emailError
                    ? "error.main"
                    : error
                      ? "error.main"
                      : "success.main",
                }}
              >
                {emailError || error || success}
              </Typography>
            )}
          </Box>

          {/* Login button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={!isFormValid}
              onClick={handleLogin}
              sx={{
                width: "190px",
                height: "40px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "18px",
                fontWeight: 600,
                color: "#FFFFFF",
                boxShadow: "none",
                backgroundColor: isFormValid ? "#489596" : "#999C9F",
                "&:hover": {
                  backgroundColor: isFormValid ? "#3a7b7f" : "#999C9F",
                  boxShadow: "none",
                },
                textTransform: "none",
              }}
            >
              Login
            </Button>
          </Box>

          {/* Links */}
          <Box mt={2}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                fontFamily: "PT Sans, sans-serif",
                fontSize: "16px",
              }}
            >
              Don’t have an account?{" "}
              <Link
                href="#"
                onClick={() => {
                  handleClose(); // close login
                  openSignup();  // open signup
                }}
                underline="none"
                sx={{
                  color: "#489596",
                  fontFamily: "PT Sans, sans-serif",
                  fontSize: "16px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Sign up here
              </Link>
            </Typography>

            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                mt: 1,
                fontFamily: "PT Sans, sans-serif",
                fontSize: "16px",
              }}
            >
              <Link
                href="#"
                underline="none"
                sx={{
                  color: "#489596",
                  fontFamily: "PT Sans, sans-serif",
                  fontSize: "16px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Forgot your password?
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default LoginModal;
